import { ILaserOrEndmill } from '@/redux/post/post.d';

/** Minimum valid spindle speed in RPM for Endmill tools. */
export const SPINDLE_SPEED_MIN = 6000;

/** Maximum valid spindle speed in RPM for Endmill tools. */
export const SPINDLE_SPEED_MAX = 24000;

/** Default spindle speed in RPM applied to a new Endmill tool. */
export const SPINDLE_SPEED_DEFAULT = 18000;

/**
 * Lowercase, dot-prefixed file extensions that identify a CNC model/toolpath
 * file. Single source of truth — used for carousel support, controller
 * filtering, tool-data matching, and upload validation. `.gc` is easy to forget
 * (it caused an empty Tools Data card), so it lives here once. Not `as const` so
 * `.includes(someString)` type-checks at call sites.
 */
export const MODEL_FILE_EXTENSIONS = ['.gcode', '.gc', '.nc', '.ngc'];

const IMG_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'];
const GCODE_EXTS = ['nc', 'gcode', 'tap', 'ngc', 'cnc', 'gc'];

export const EXTS_BY_KIND = { image: IMG_EXTS, gcode: GCODE_EXTS };

export const CAM_PROJECT_EXTS = [
  'crv',
  'crv3d',
  'art',
  'c2d',
  'easel',
  'vcarve',
  'v3m',
  'lbn',
  'lbn2',
  'lbrn',
  'lbrn2',
];

export const MODEL_3D_EXTS = [
  'stl',
  'step',
  'stp',
  'f3d',
  'obj',
  '3mf',
  'iges',
  'igs',
  'sldprt',
];

// Vectors are artwork, not a re-openable CAM/model source.
export const CAD_EXTS = [...CAM_PROJECT_EXTS, ...MODEL_3D_EXTS];

export const isCadExt = (ext: string): boolean => CAD_EXTS.includes(ext);

/** Which of the three buckets a file belongs to, by extension and nothing else.
 * The upload wizard's sections and the post carousel's slide order both read
 * from this, so a `.svg` is a doc in one exactly as it is in the other. */
export function kindOf(ext: string): 'image' | 'gcode' | 'doc' {
  if (IMG_EXTS.includes(ext)) return 'image';
  if (GCODE_EXTS.includes(ext)) return 'gcode';
  return 'doc';
}

/**
 * Returns true when the tool row satisfies its bit requirement.
 * Both Endmill rows (a catalog bit) and Laser rows (which laser to use) must
 * reference a catalog bit via `bitId`.
 */
export const toolHasBit = (tool: {
  laserOrEndmill: string | ILaserOrEndmill;
  bitId?: string;
}): boolean => !!tool.bitId;
